import type { FairValueHistogramBucket } from '../fairValueHistogramFragment';
import { Group } from '@visx/group';
import { BarRounded } from '@visx/shape';
import { ScaleBand, ScaleLinear } from 'd3-scale';
import { cn } from '@simplywallst/ui-core/next';
import { Fragment, useId, useMemo } from 'react';

type Props = {
  xBandScale: ScaleBand<number>;
  yScale: ScaleLinear<number, number, never>;
  buckets: FairValueHistogramBucket[];
  height: number;
  selectedBucket: FairValueHistogramBucket | null;
};

export const FairValueHistogramChartBars = ({
  xBandScale,
  yScale,
  buckets,
  height,
  selectedBucket,
}: Props) => {
  const radialGradientId = useId();
  const maskId = useId();
  const bandwidth = xBandScale.bandwidth();
  const tallestBucketXPercentage = useMemo(() => {
    const tallestBucketX =
      xBandScale(buckets.reduce((a, b) => (a.count > b.count ? a : b)).min) ??
      0 + bandwidth / 2;

    const xMax = xBandScale.range()[1];
    return xMax
      ? Math.max(0, Math.min(100, (tallestBucketX / xMax) * 100))
      : 50;
  }, [buckets, bandwidth, xBandScale]);

  return (
    <>
      <defs>
        <radialGradient
          id={radialGradientId}
          cx={`${tallestBucketXPercentage}%`}
          cy="50%"
          r="70%"
          fx={`${tallestBucketXPercentage}%`}
          fy="50%"
        >
          <stop
            offset="20%"
            stopColor="var(--s-color-p-grey-100)"
            stopOpacity="0"
          />
          <stop
            offset="100%"
            stopColor="var(--s-color-p-grey-100)"
            stopOpacity="0.6"
          />
        </radialGradient>
        <mask id={maskId}>
          {buckets.map((bucket, i) => (
            <Group key={`bar-${i}`}>
              <BarRounded
                x={xBandScale(bucket.min) ?? 0}
                y={yScale(bucket.count)}
                width={bandwidth}
                height={height - yScale(bucket.count)}
                className="fill-p-white-100"
                top
                radius={4}
              />
            </Group>
          ))}
        </mask>
      </defs>

      <Group>
        {buckets.map((bucket, i) => {
          const isSelected = selectedBucket?.min === bucket.min;
          const y = yScale(bucket.count);
          return (
            <Fragment key={`bar-${i}`}>
              <BarRounded
                x={xBandScale(bucket.min) ?? 0}
                y={y}
                width={bandwidth}
                height={height - y}
                className={cn(
                  'transition-colors',
                  isSelected ? 'fill-chart-03-soft' : 'fill-chart-03'
                )}
                top
                radius={4}
              />

              <rect
                x={xBandScale(bucket.min) ?? 0}
                width={bandwidth}
                y={height}
                height={3}
                className={cn(
                  'transition-colors',
                  isSelected ? 'fill-brand-01 opacity-70' : 'fill-transparent'
                )}
              />
            </Fragment>
          );
        })}
        <rect
          x="0"
          y="0"
          width="100%"
          height={height}
          fill={`url(#${radialGradientId})`}
          mask={`url(#${maskId})`}
        />
      </Group>
    </>
  );
};
